import { useQuery } from '@tanstack/react-query';
import {
  Cluster,
  Heading,
  Spinner,
  Stack,
  StatusBadge,
  Surface,
  Text,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { absoluteTime, relativeTime, shortId } from '../lib/pollerActivity';
import { PollerConnectionsTable } from './PollerConnectionsTable';

const HEALTH_QUERY_KEY = (id: string) => ['admin', 'poller-connection-health', id] as const;

/**
 * Connection fetch-health panel (#318): the durable poll cursor position(s) for one connection
 * plus its recent per-tick fetch rollups, from the operator-gated
 * `GET /poller-connections/:id/health`. This is the surface that makes "we never fetched this
 * mailbox" visible — no cursor and no recent ticks means the poller never reached it.
 */
export function PollerConnectionHealthPanel({ connectionId }: { connectionId: string }) {
  const query = useQuery({
    queryKey: HEALTH_QUERY_KEY(connectionId),
    queryFn: () => api.getPollerConnectionHealth(connectionId),
    retry: false,
  });

  return (
    <Surface bordered radius="lg" padding="5" data-testid="poller-connection-health">
      <Stack gap="4">
        <Stack gap="1">
          <Heading level={2} size="sm">
            Connection fetch health
          </Heading>
          <Text size="sm" tone="secondary" title={connectionId} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {shortId(connectionId, 16)}
          </Text>
        </Stack>

        {query.isPending ? (
          <Cluster gap="2" align="center">
            <Spinner size="sm" />
            <Text size="sm" tone="muted">
              Loading connection health…
            </Text>
          </Cluster>
        ) : query.isError || !query.data ? (
          <Text size="sm" tone="danger">
            {query.error instanceof ApiError
              ? query.error.message
              : 'Could not load connection health.'}
          </Text>
        ) : (
          <Stack gap="4">
            <Stack gap="2">
              <Text size="xs" tone="muted" weight="semibold">
                Poll cursors ({query.data.cursors.length})
              </Text>
              {query.data.cursors.length === 0 ? (
                <Surface tone="subtle" bordered radius="md" padding="4">
                  <Stack gap="1">
                    <StatusBadge size="sm" tone="warning" label="Never fetched" />
                    <Text size="sm" tone="secondary" data-testid="poller-health-never-fetched">
                      No poll cursor recorded for this connection — the poller has never
                      successfully fetched from this mailbox.
                    </Text>
                  </Stack>
                </Surface>
              ) : (
                <Stack gap="2" role="list" data-testid="poller-health-cursors">
                  {query.data.cursors.map((c) => (
                    <Surface key={c.stream} tone="subtle" bordered radius="md" padding="3" role="listitem">
                      <Cluster gap="3" align="center" justify="between" wrap>
                        <Text as="span" size="sm" weight="medium">
                          {c.stream}
                        </Text>
                        <Text
                          as="span"
                          size="sm"
                          tone={c.cursor ? 'secondary' : 'muted'}
                          truncate
                          title={c.cursor ?? undefined}
                          style={{ maxWidth: '18rem', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {c.cursor ?? 'no cursor'}
                        </Text>
                        <Text as="span" size="sm" tone="muted" title={absoluteTime(c.updatedAt)}>
                          {relativeTime(c.updatedAt)}
                        </Text>
                      </Cluster>
                    </Surface>
                  ))}
                </Stack>
              )}
            </Stack>

            <Stack gap="2">
              <Text size="xs" tone="muted" weight="semibold">
                Recent fetches ({query.data.recent.length})
              </Text>
              <PollerConnectionsTable
                connections={query.data.recent}
                hideConnection
                caption="Recent per-tick fetch diagnostics for this connection"
                emptyLabel="No recent fetches recorded for this connection."
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </Surface>
  );
}
